import React, { createContext, useContext, ReactNode, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useAppSelector, useAppDispatch } from "@/state/hooks";
import { UserProfiles } from "@/api";
import { isTokenExpired } from "@/utils/tokenUtils";
import { clearToken, clearClerkId, clearProfile, setClerkId, setToken } from "@/state/token/clientTokenSlice";
import { fetchUserProfileThunk } from "@/state/userProfile/userProfileSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import { getIntelligenicToken } from "./getIntelligenicToken";

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserProfiles | null;
    error: string | null;
    retryAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { getToken, isSignedIn, isLoaded, signOut, userId } = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profileBootstrapInFlight = useRef(false);
    const isProtectedRoute = location.pathname === "/dashboard"
        || location.pathname.startsWith("/dashboard/")
        || location.pathname === "/products"
        || location.pathname.startsWith("/products/")
        || location.pathname === "/context-meshes"
        || location.pathname.startsWith("/context-meshes/")
        || location.pathname === "/files"
        || location.pathname === "/integrations";

    const userProfile = useAppSelector(state => state.userProfiles.userProfile);
    const userProfileStatus = useAppSelector(state => state.userProfiles.status);
    const token = useAppSelector(state => state.clientToken.token);

    // A hard refresh can abort the profile request after Redux has persisted
    // its transient `loading` state. Protected routes sit behind AuthGuard, so
    // the Interface component cannot repair that orphaned request. Bootstrap
    // the profile here, above the guard, for direct protected-route loads.
    useEffect(() => {
        if (
            !isProtectedRoute
            || !isLoaded
            || !isSignedIn
            || userProfile
            || !["idle", "loading"].includes(userProfileStatus)
            || profileBootstrapInFlight.current
        ) {
            return;
        }

        profileBootstrapInFlight.current = true;
        const bootstrapProfile = async () => {
            try {
                const authToken = await getIntelligenicToken(getToken);
                if (!authToken || !userId) {
                    dispatch(clearToken());
                    dispatch(clearClerkId());
                } else {
                    dispatch(setToken(authToken));
                    dispatch(setClerkId(userId));
                }
                await dispatch(fetchUserProfileThunk()).unwrap();
            } catch (cause) {
                console.error("AuthProvider: Protected-route profile bootstrap failed:", cause);
            } finally {
                profileBootstrapInFlight.current = false;
            }
        };
        void bootstrapProfile();
    }, [dispatch, getToken, isLoaded, isProtectedRoute, isSignedIn, userId, userProfile, userProfileStatus]);

    // Use token refresh hook
    useTokenRefresh();

    const handleLogout = useCallback(async () => {
        // Clear Redux state
        dispatch(clearToken());
        dispatch(clearClerkId());
        dispatch(clearProfile());

        // Sign out from Clerk
        await signOut();

        // Navigate to splash
        navigate("/splash", { replace: true });
    }, [dispatch, signOut, navigate]);

    // Check token expiration periodically
    useEffect(() => {
        if (!token) return;

        // Check immediately
        if (isTokenExpired(token)) {
            console.log("Token expired, logging out");
            handleLogout();
            return;
        }

        // Check every 30 seconds
        const interval = setInterval(() => {
            if (isTokenExpired(token)) {
                console.log("Token expired during session, logging out");
                handleLogout();
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [token, handleLogout]);

    // More nuanced loading state that accounts for profile loading
    const isLoading = !isLoaded || (
        isSignedIn
        && isProtectedRoute
        && !userProfile
        && ["idle", "loading"].includes(userProfileStatus)
    );

    const isAuthenticated = isSignedIn && !!token && !isTokenExpired(token) && (userProfileStatus === "succeeded" || userProfileStatus === "loading");

    const retryAuth = useCallback(() => {
        // Placeholder for retry logic if needed in the future
    }, []);

    const contextValue: AuthContextType = useMemo(() => ({
        isAuthenticated: isAuthenticated ?? false,
        isLoading: isLoading ?? false,
        user: userProfile,
        error: userProfileStatus === "failed" ? "Failed to load user profile" : null,
        retryAuth
    }), [isAuthenticated, isLoading, userProfile, userProfileStatus, retryAuth]);

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
